import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import calendarService from '../services/calendarService';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths } from 'date-fns';

const CalendarContext = createContext();

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

export const CalendarProvider = ({ children }) => {
  const [searchParams] = useSearchParams();

  // Available calendars for the user
  const [availableCalendars, setAvailableCalendars] = useState([]);

  // Selected calendar IDs (visible in the view)
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(() => {
    const saved = localStorage.getItem('selectedCalendarIds');
    return saved ? JSON.parse(saved) : [];
  });

  // Events from selected calendars
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  // View settings
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem('calendarViewType') || 'agenda';
  });

  // Date range for filtering
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('calendarDateRange');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        start: parsed.start ? new Date(parsed.start) : new Date(),
        end: parsed.end ? new Date(parsed.end) : new Date()
      };
    }
    // Default to current week
    const today = new Date();
    return {
      start: startOfWeek(today),
      end: endOfWeek(today)
    };
  });

  // Time filter (upcoming, today, this_week, this_month, custom)
  const [timeFilter, setTimeFilter] = useState(() => {
    return localStorage.getItem('calendarTimeFilter') || 'this_week';
  });

  // Loading states
  const [calendarsLoading, setCalendarsLoading] = useState(true);

  /**
   * Load available calendars on mount
   */
  useEffect(() => {
    loadAvailableCalendars();
  }, []);

  /**
   * Handle calendar_id URL parameter
   * This implements the event management flow from CALENDAR_SYSTEM_REFACTOR_PLAN.md
   * When admin clicks "Manage Events", they navigate to /calendar?calendar_id=xxx
   * and this calendar is pre-selected
   */
  useEffect(() => {
    const calendarIdParam = searchParams.get('calendar_id');

    if (calendarIdParam && availableCalendars.length > 0) {
      // Check if this calendar exists in available calendars
      const calendarExists = availableCalendars.some(cal => cal.id === calendarIdParam);

      if (calendarExists) {
        console.log('[CalendarContext] URL parameter calendar_id detected:', calendarIdParam);
        // Pre-select this calendar (replace current selection)
        setSelectedCalendarIds([calendarIdParam]);
        localStorage.setItem('selectedCalendarIds', JSON.stringify([calendarIdParam]));
      } else {
        console.warn('[CalendarContext] Calendar ID from URL not found in available calendars:', calendarIdParam);
      }
    }
  }, [searchParams, availableCalendars]);

  /**
   * Load available calendars for the user
   */
  const loadAvailableCalendars = async () => {
    try {
      setCalendarsLoading(true);
      console.log('[CalendarContext] Loading available calendars...');
      const calendars = await calendarService.getAvailableCalendars();
      console.log('[CalendarContext] Received calendars:', calendars);

      // Ensure calendars is always an array
      const calendarList = Array.isArray(calendars) ? calendars : [];
      console.log('[CalendarContext] Calendar list (array):', calendarList.length, 'calendars');
      setAvailableCalendars(calendarList);

      // Auto-select all calendars if none selected
      if (selectedCalendarIds.length === 0 && calendarList.length > 0) {
        const allIds = calendarList.map(cal => cal.id);
        console.log('[CalendarContext] Auto-selecting all calendars:', allIds);
        setSelectedCalendarIds(allIds);
        localStorage.setItem('selectedCalendarIds', JSON.stringify(allIds));
      }

      // If no calendars available, clear selected IDs
      if (calendarList.length === 0 && selectedCalendarIds.length > 0) {
        console.log('[CalendarContext] No calendars available, clearing selected IDs');
        setSelectedCalendarIds([]);
        localStorage.removeItem('selectedCalendarIds');
      }
    } catch (error) {
      console.error('[CalendarContext] Error loading available calendars:', error);
      setAvailableCalendars([]);
    } finally {
      setCalendarsLoading(false);
    }
  };

  /**
   * Reload events when selected calendars or filters change
   */
  useEffect(() => {
    if (selectedCalendarIds.length > 0) {
      loadEvents();
    } else {
      setEvents([]);
    }
  }, [selectedCalendarIds, timeFilter, dateRange.start, dateRange.end]);

  /**
   * Load events from selected calendars
   */
  const loadEvents = async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);

      const filters = {
        view_type: viewType
      };

      // For custom date ranges, send start_date and end_date without time_filter
      // Otherwise use the time_filter
      if (timeFilter === 'custom' && dateRange) {
        // Send full ISO datetime strings (UTC) for proper backend filtering
        const startOfDay = new Date(dateRange.start);
        startOfDay.setUTCHours(0, 0, 0, 0);
        filters.start_date = startOfDay.toISOString();

        const endOfDay = new Date(dateRange.end);
        endOfDay.setUTCHours(23, 59, 59, 999);
        filters.end_date = endOfDay.toISOString();
        // Don't send time_filter when using custom date range
      } else if (timeFilter && timeFilter !== 'custom') {
        // Use predefined time filter
        filters.time_filter = timeFilter;
      }

      console.log('[CalendarContext] Loading events with filters:', filters);
      const response = await calendarService.getMultiCalendarView(selectedCalendarIds, filters);
      console.log('[CalendarContext] Loaded events:', response.events?.length || 0);
      setEvents(response.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
      setEventsError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  /**
   * Toggle calendar visibility
   */
  const toggleCalendar = useCallback((calendarId) => {
    setSelectedCalendarIds(prev => {
      const newIds = prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId];

      localStorage.setItem('selectedCalendarIds', JSON.stringify(newIds));
      return newIds;
    });
  }, []);

  /**
   * Select/deselect all calendars
   */
  const toggleAllCalendars = useCallback((select = true) => {
    const newIds = select ? availableCalendars.map(cal => cal.id) : [];
    setSelectedCalendarIds(newIds);
    localStorage.setItem('selectedCalendarIds', JSON.stringify(newIds));
  }, [availableCalendars]);

  /**
   * Update view type
   */
  const updateViewType = useCallback((newViewType) => {
    setViewType(newViewType);
    localStorage.setItem('calendarViewType', newViewType);
  }, []);

  /**
   * Update time filter
   */
  const updateTimeFilter = useCallback((newTimeFilter) => {
    setTimeFilter(newTimeFilter);
    localStorage.setItem('calendarTimeFilter', newTimeFilter);

    // Update date range based on filter
    const today = new Date();
    let newRange = { start: today, end: today };

    switch (newTimeFilter) {
      case 'all':
        // Show all events - no date restriction
        newRange = null;
        break;
      case 'today':
        newRange = { start: today, end: today };
        break;
      case 'this_week':
        newRange = { start: startOfWeek(today), end: endOfWeek(today) };
        break;
      case 'next_week':
        const nextWeek = addWeeks(today, 1);
        newRange = { start: startOfWeek(nextWeek), end: endOfWeek(nextWeek) };
        break;
      case 'this_month':
        newRange = { start: startOfMonth(today), end: endOfMonth(today) };
        break;
      case 'next_month':
        const nextMonth = addMonths(today, 1);
        newRange = { start: startOfMonth(nextMonth), end: endOfMonth(nextMonth) };
        break;
      case 'upcoming':
        newRange = { start: today, end: addMonths(today, 3) };
        break;
      case 'past':
        // Show all past events - backend will handle this
        newRange = null;
        break;
      default:
        // Custom - keep existing range
        return;
    }

    if (newRange) {
      setDateRange(newRange);
      localStorage.setItem('calendarDateRange', JSON.stringify({
        start: newRange.start.toISOString(),
        end: newRange.end.toISOString()
      }));
    } else {
      // For 'all' and 'past', remove the date range restriction
      localStorage.removeItem('calendarDateRange');
    }
  }, []);

  /**
   * Update custom date range
   */
  const updateDateRange = useCallback((start, end) => {
    const newRange = { start, end };
    setDateRange(newRange);
    setTimeFilter('custom');
    localStorage.setItem('calendarDateRange', JSON.stringify({
      start: start.toISOString(),
      end: end.toISOString()
    }));
    localStorage.setItem('calendarTimeFilter', 'custom');
  }, []);

  /**
   * Get calendar by ID
   */
  const getCalendarById = useCallback((calendarId) => {
    return availableCalendars.find(cal => cal.id === calendarId);
  }, [availableCalendars]);

  /**
   * Check if calendar is selected
   */
  const isCalendarSelected = useCallback((calendarId) => {
    return selectedCalendarIds.includes(calendarId);
  }, [selectedCalendarIds]);

  /**
   * Get events for a specific calendar
   */
  const getEventsForCalendar = useCallback((calendarId) => {
    return events.filter(event => event.calendar_id === calendarId);
  }, [events]);

  /**
   * Refresh data
   */
  const refresh = useCallback(async () => {
    await loadAvailableCalendars();
    if (selectedCalendarIds.length > 0) {
      await loadEvents();
    }
  }, [selectedCalendarIds]);

  const value = {
    // Calendars
    availableCalendars,
    calendarsLoading,
    selectedCalendarIds,
    toggleCalendar,
    toggleAllCalendars,
    getCalendarById,
    isCalendarSelected,

    // Events
    events,
    eventsLoading,
    eventsError,
    getEventsForCalendar,
    loadEvents,

    // View settings
    viewType,
    updateViewType,
    timeFilter,
    updateTimeFilter,
    dateRange,
    updateDateRange,

    // Actions
    refresh,
    loadAvailableCalendars
  };

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};
